import { BREAKPOINTS, COLORS, HyperLink, LoadingIndicator, PageWidth, SystemIcons } from '@laerdal/life-react-components';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import posthog from 'posthog-js';
import ProductListItem from '../../../commonComponents/ProductListItem';
import { useSelector } from 'react-redux';
import { selectMarket } from '../../../store/slices/globalConfigSlice';
import { useDebouncedValue } from 'rooks';
import SearchApi from '../../../services/api/SearchApi';
import { LandingSearchResponseDto } from '../../../model/dto/landingSearchResponseDto';
import { selectIsAuthenticated } from '../../../store/slices/userSlice';

const SearchOverlay = styled.div<{ $isVisible: boolean }>`
  display: ${props => props.$isVisible ? 'block' : 'none'};
  position: fixed;
  top: 0px;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #2826265E;
  z-index: 999;
  pointer-events: auto;
`;

const SearchDropdownContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 50px;
`;

const SearchDropdown = styled.div<{ $isVisible: boolean }>`
  display: ${props => props.$isVisible ? 'block' : 'none'};
  position: absolute;
  top: calc(100% + 0px);
  left: 0;
  width: 100%;
  z-index: 1000;
  
  
`;

const SearchContainer = styled.div`
  margin: 16px 20px;
  padding: 24px;
  background-color: white;
  border-radius: 8px;
  gap: 24px;
  display: flex;
  flex-direction: column;
`

const GrayGradient = styled.div`
  z-index: 0;
  position: absolute;
  width:100%;
  height:100%;
`;

const GrayGradientTop = styled.div`
  height:50%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 69.5%, rgba(0, 0, 0, 0.50) 100%);
`;

const GrayGradientBottom = styled.div`
  height:50%;
  background: var(--overlay-black-50, rgba(0, 0, 0, 0.50));
`;
const HeaderContainer = styled.div`
  
  height: 441px;

  display: flex;
  position: relative;
  
  background: url('assets/LandingHeaderBackground.webp') lightgray;
  background-size: cover;
  background-position: center; 
  background-repeat: no-repeat;
  width: 100%;
`;

const TopFrame = styled(PageWidth)`
  ${BREAKPOINTS.LARGE}{
    width: 1024px;
    margin: 72px auto 32px auto;  
  }
  padding:24px;
    
  gap:16px;
  flex-grow: 1;  
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-self: start;
`;

const TopFrameText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap:16px;
`;

const DesktopH3 = styled.div`
  
  font-size: 64px;
  

  color: ${COLORS.white};
  font-family: Lato;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 48px */
`;

const DesktopH4 = styled.div`
  font-size: 18px;
  ${BREAKPOINTS.LARGE}{
    font-size: 38px;
  }

  color: ${COLORS.white};
  font-family: Lato;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 48px */
`;

const SearchInputWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  width: 100%;
  z-index: 1001;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 16px 14px 48px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 25px;
  outline: none;
  font-family: Lato;
  
  &:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
  
  &::placeholder {
    color: #999;
  }
`;


const SearchChunk = styled.div`
  
`;
const SearchTitle = styled.div`
  color: ${COLORS.neutral_800};
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  padding: 16px 0;
  
`;
const SearchGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  ${BREAKPOINTS.MEDIUM} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 24px;
    row-gap: 8px;
  }
`;

const SearchArticles = styled.div`
`
const SearchArticlesItem = styled.div`
  color: ${COLORS.neutral_800};
  font-family: Lato;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const SearchLink = styled(HyperLink)`
  color: ${COLORS.neutral_800} !important;
  text-decoration-line: underline;    
  
  &:focus {
    color: ${COLORS.accent1} !important;
    background-color: unset;
    text-decoration-line: underline !important;    
  }

  &:hover {
    color: ${COLORS.accent1} !important;
    background-color: unset;
    text-decoration-line: underline !important;    
  }
  
  svg {
      vertical-align: middle;
      margin: 0 8px 4px 0;
  }

  font-size: 16px;
  ${BREAKPOINTS.MEDIUM} {
    font-size: 20px;
    }
`;



const LandingHeader = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const [searchData, setSearchData] = useState<LandingSearchResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation("landingPage");
  const currentMarket = useSelector(selectMarket);
    const isAuthenticated = useSelector(selectIsAuthenticated);
  const [searchTermDebounced] = useDebouncedValue(searchTerm, 300);
  const preloadedDataRef = useRef<LandingSearchResponseDto | null>(null);

  // Preload search data for empty search term on mount
  useEffect(() => {
    const abortController = new AbortController();
    SearchApi.MainSearch('', abortController.signal)
      .then((data) => {
        if (!abortController.signal.aborted) {
          preloadedDataRef.current = data;
          // If user already focused before preload finished, show data immediately
          if (isSearchFocused && !searchTerm) {
            setSearchData(data);
            setIsLoading(false);
          }
        }
      })
      .catch((error) => {
        if (!abortController.signal.aborted) {
          console.error('Failed to preload search data:', error);
        }
      });
    return () => abortController.abort();
  }, []);

  // Fetch search data when debounced term changes or on focus
  useEffect(() => {
    if (isSearchFocused) {
      // For empty search term, use preloaded data if available
      if (!searchTermDebounced && preloadedDataRef.current) {
        setSearchData(preloadedDataRef.current);
        setIsLoading(false);
        return;
      }

      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsLoading(true);

      SearchApi.MainSearch(searchTermDebounced || '', abortController.signal)
        .then((data) => {
          if (!abortController.signal.aborted) {
            
            posthog.capture('SUPP-AllSearch', {
              searchTerm: searchTermDebounced,
              page: 'LandingPage',
              isAuthenticated: isAuthenticated
            });

            posthog.capture('SUPP-LandingPage-Search', {
              searchTerm: searchTermDebounced,
              page: 'LandingPage',
              isAuthenticated: isAuthenticated
            });

            setSearchData(data);
            setIsLoading(false);
            // Cache empty search result for future use
            if (!searchTermDebounced) {
              preloadedDataRef.current = data;
            }
          }
        })
        .catch((error) => {
          if (!abortController.signal.aborted) {
            console.error('Failed to fetch search data:', error);
            setIsLoading(false);
          }
        });
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchTermDebounced, isSearchFocused]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    if (isSearchFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchFocused]);

  return (
    <>

      <HeaderContainer>
        <GrayGradient>
          <GrayGradientTop></GrayGradientTop>
          <GrayGradientBottom></GrayGradientBottom>
        </GrayGradient>

        <TopFrame
          $useMaxWidth={true}
          $maxWidth={1024}
        >
          <TopFrameText>
            <DesktopH3>{t('Help Center')}</DesktopH3>
            <DesktopH4>{t('What do you need help with?')}</DesktopH4>
          </TopFrameText>
          <SearchDropdownContainer
            ref={searchRef}
            onBlur={(e) => {
              // Check if the newly focused element is outside the search container
              if (!searchRef.current?.contains(e.relatedTarget as Node)) {
                setIsSearchFocused(false);
              }
            }}
          >
            <SearchInputWrapper>
              <SearchIconWrapper>
                <SystemIcons.Search color={COLORS.neutral_500} />
              </SearchIconWrapper>
              <SearchInput
                type="text"
                placeholder={t('Type a question or search by keyword')}
                value={searchTerm}
                onChange={(e) => {
                  const term = e.target.value;
                  setSearchTerm(term);
                  if (term.length > 0 || isSearchFocused) {
                    setIsSearchFocused(true);
                  }
                }}
                onFocus={() => setIsSearchFocused(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchTerm.trim()) {
                    posthog.capture('LandingPage Search', {
                      searchTerm: searchTerm
                    });
                    navigate('articles?query=' + searchTerm);
                  }
                }}
              />
            </SearchInputWrapper>
            <SearchOverlay $isVisible={isSearchFocused} onClick={() => setIsSearchFocused(false)} />
            <SearchDropdown $isVisible={isSearchFocused}>
              <SearchContainer>
                {isLoading ? (
                  <LoadingIndicator />
                ) : searchData ? (
                  searchData.products.length === 0 &&
                    searchData.articles.length === 0 ? (
                    <SearchTitle>{t('No search results')}</SearchTitle>
                  ) : (
                    <>
                      {searchData.articles.length > 0 && (
                        <SearchChunk>
                          <SearchTitle>{t('Articles')}</SearchTitle>
                          <SearchArticles>
                            <SearchArticlesItem>
                              {searchData.articles.map((article, index) => {
                                const url = `/articles/${article.articleNumber}`;
                                return (
                                  <SearchLink
                                    key={index}
                                    color='red'
                                    href={currentMarket ? `/${currentMarket}${url}` : url}
                                    variant='default'
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(currentMarket ? `/${currentMarket}${url}` : url);
                                    }}
                                  >
                                    <SystemIcons.Search size='20px' color={COLORS.neutral_800} />
                                    {article.title}
                                  </SearchLink>
                                );
                              })}
                            </SearchArticlesItem>
                          </SearchArticles>
                        </SearchChunk>
                      )}

                      {searchData.products.length > 0 && (
                        <SearchChunk>
                          <SearchTitle>{t('Popular Products')}</SearchTitle>
                          <SearchGrid>
                            {searchData.products.map((product, index) => {
                              const productUrl = product.redirectUrl
                                ? product.redirectUrl
                                : (product.categoryLink && product.linkName
                                  ? `/category/${product.categoryLink}/product/${product.linkName}`
                                  : null);
                              return (
                                <ProductListItem
                                  key={index}
                                  title={product.title}
                                  imageUrl={product.imageUrl}
                                  onClick={() => productUrl && navigate(currentMarket ? `/${currentMarket}${productUrl}` : productUrl)}
                                />
                              );
                            })}
                          </SearchGrid>
                        </SearchChunk>
                      )}
                    </>
                  )
                ) : null}
              </SearchContainer>
            </SearchDropdown>
          </SearchDropdownContainer>
        </TopFrame>
      </HeaderContainer>
    </>
  );
}

export default LandingHeader;